<template>
  <v-container fluid class="pa-4">
    <!-- Header & Action Bar -->
    <v-row class="mb-2 align-center justify-space-between">
      <v-col cols="12" md="6" class="d-flex align-center">
        <v-icon color="indigo darken-2" large class="mr-3">mdi-view-column</v-icon>
        <div>
          <h2 class="headline font-weight-bold indigo--text text--darken-3">Tablero Kanban de Historias de Usuario TI</h2>
          <div class="caption text-uppercase grey--text text--darken-1">Seguimiento Operativo, Bloqueos y Arrastre Interactivo (Drag & Drop)</div>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="text-right d-flex align-center justify-end">
        <v-btn color="indigo" dark class="elevation-2" @click="loadKanbanData">
          <v-icon left>mdi-refresh</v-icon> Actualizar Tablero
        </v-btn>
      </v-col>
    </v-row>

    <!-- Barra de Filtros por Sprint, Fechas y Rol -->
    <v-card outlined class="pa-3 mb-4 rounded-lg grey lighten-5 elevation-1">
      <v-row dense class="align-center">
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="selectedSprintFilter"
            :items="sprintFilterOptions"
            label="Filtrar por Sprint"
            dense
            outlined
            hide-details
            @change="applySprintFilter"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="3" md="2">
          <s-date label="Desde (Req)" v-model="filter.ReqDateFrom" clearable @change="applyDateFilter"></s-date>
        </v-col>
        <v-col cols="12" sm="3" md="2">
          <s-date label="Hasta (Req)" v-model="filter.ReqDateUntil" clearable @change="applyDateFilter"></s-date>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <s-text label="Buscar Título / Código" v-model="filter.UsoTitle" placeholder="Palabra clave..." @keyup.enter="loadKanbanData"></s-text>
        </v-col>
        <v-col cols="6" sm="3" md="1">
          <v-switch
            v-model="myTasksOnly"
            label="Mis Tareas"
            color="indigo"
            dense
            hide-details
            class="mt-0 font-weight-bold"
            @change="loadKanbanData"
          ></v-switch>
        </v-col>
        <v-col cols="6" sm="3" md="1">
          <v-switch
            v-model="filter.ExcludeTerminalStates"
            label="Ocultar Terminados"
            color="teal"
            dense
            hide-details
            class="mt-0 font-weight-bold"
            @change="loadKanbanData"
          ></v-switch>
        </v-col>
      </v-row>
    </v-card>

    <!-- Columnas del Tablero Kanban (Layout Horizontal con Drag & Drop) -->
    <div class="kanban-scroll-wrapper">
      <v-row class="kanban-row flex-nowrap overflow-x-auto pb-4 ma-0">
        <div v-for="col in columns" :key="col.id" class="kanban-column-item pa-2">
          <v-card outlined class="grey lighten-4 fill-height rounded-lg border-top-accent elevation-1" :style="{ borderTopColor: col.color }">
            <!-- Header de Columna -->
            <div class="pa-3 d-flex align-center justify-space-between border-bottom white">
              <div class="d-flex align-center">
                <v-icon small class="mr-1" :color="col.color">{{ col.icon }}</v-icon>
                <span class="subtitle-2 font-weight-bold grey--text text--darken-3">{{ col.title }}</span>
              </div>
              <v-chip x-small :color="col.color" dark class="font-weight-bold">
                {{ getColumnItems(col.status).length }}
              </v-chip>
            </div>

            <!-- Cuerpo de Columna (Tarjetas Arrastrables via vuedraggable) -->
            <div class="pa-2 kanban-column-body">
              <draggable
                :list="getColumnItems(col.status)"
                group="kanban"
                ghost-class="ghost-card"
                class="kanban-draggable-zone fill-height"
                @change="onCardMoved($event, col.status)"
              >
                <v-card
                  v-for="item in getColumnItems(col.status)"
                  :key="item.UsoID"
                  outlined
                  class="mb-3 rounded-lg hover-card elevation-1 white cursor-move"
                  :class="{ 'border-red-blocked': item.UsoIsBlocked }"
                >
                  <div class="pa-3">
                    <!-- Header Tarjeta -->
                    <div class="d-flex justify-space-between align-center mb-1">
                      <span class="caption font-weight-bold primary--text">{{ item.UsoCode }}</span>
                      <div class="d-flex align-center">
                        <v-chip v-if="item.UsoStoryPoints" x-small color="deep-purple lighten-4" class="deep-purple--text font-weight-bold mr-1">
                          {{ item.UsoStoryPoints }} pts
                        </v-chip>
                        <v-chip v-if="item.SprintName" x-small color="indigo lighten-4" class="indigo--text font-weight-bold mr-1">
                          {{ item.SprintName }}
                        </v-chip>
                        <v-chip v-if="item.UsoIsBlocked" x-small color="red" dark class="font-weight-bold">
                          <v-icon x-small left>mdi-lock</v-icon> BLOQUEADO
                        </v-chip>
                      </div>
                    </div>

                    <!-- Título de la Historia de Usuario -->
                    <div class="body-2 font-weight-bold text--primary mb-2 line-clamp-2" :title="item.UsoTitle">
                      {{ item.UsoTitle }}
                    </div>

                    <!-- Requerimiento padre -->
                    <div class="caption grey--text text--darken-2 mb-1 line-clamp-1" :title="item.ReqTitle">
                      <v-icon x-small color="grey" class="mr-1">mdi-link-variant</v-icon>
                      {{ item.ReqCode }} - {{ item.ReqTitle }}
                    </div>

                    <!-- Responsable Asignado -->
                    <div class="caption grey--text text--darken-2 mb-1 d-flex align-center">
                      <v-icon x-small color="grey" class="mr-1">mdi-account-circle</v-icon>
                      <span class="text-truncate">{{ item.AssignedITName || 'Sin Asignar' }}</span>
                    </div>

                    <!-- Cumplimiento y Desviación -->
                    <div v-if="item.UsoCommitmentDate" class="d-flex align-center justify-space-between my-2">
                      <span class="caption grey--text"><v-icon x-small left>mdi-calendar-clock</v-icon>{{ formatDate(item.UsoCommitmentDate) }}</span>
                      <v-chip
                        x-small
                        :color="getDeviationColor(item.DeviationDays)"
                        dark
                        class="font-weight-bold"
                      >
                        {{ getComplianceLabel(item.DeviationDays) }}
                      </v-chip>
                    </div>

                    <!-- Botones de Acción Operativa -->
                    <v-divider class="my-2"></v-divider>
                    <div class="d-flex justify-space-between align-center">
                      <v-btn x-small text color="indigo" @click.stop="openBlockDialog(item)">
                        <v-icon x-small left>mdi-alert-circle-outline</v-icon>
                        {{ item.UsoIsBlocked ? 'Desbloquear / Editar' : 'Gestionar Bloqueo' }}
                      </v-btn>
                    </div>
                  </div>
                </v-card>
              </draggable>
            </div>
          </v-card>
        </div>
      </v-row>
    </div>

    <!-- Modal 1: Intercepción de Avance (Transition Dialog) -->
    <v-dialog v-model="transitionDialog" max-width="650px" persistent>
      <v-card v-if="movedState" class="rounded-lg">
        <v-card-title class="headline indigo lighten-5 indigo--text text--darken-3 d-flex align-center">
          <v-icon color="indigo" class="mr-2">mdi-arrow-right-bold-circle</v-icon>
          Avance de Fase: {{ movedState.item.UsoCode }}
        </v-card-title>

        <v-card-text class="pa-4">
          <v-alert type="info" text dense class="mb-4">
            Cambiando fase de <strong>{{ getStatusName(movedState.oldStatus) }}</strong> a <strong>{{ getStatusName(movedState.newStatus) }}</strong>. Complete los datos para continuar.
          </v-alert>

          <v-row dense>
            <!-- Campos para Fase 3: Listo para Desarrollo -->
            <template v-if="movedState.newStatus === 3">
              <v-col cols="12" sm="6">
                <v-select
                  v-model="movedState.item.UsoSprintID"
                  :items="assignableSprints"
                  item-text="TspName"
                  item-value="TspID"
                  label="Sprint Asignado"
                  dense
                  outlined
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <s-select-user label="Desarrollador Asignado TI" v-model="movedState.item.UsoAssignedIT"></s-select-user>
              </v-col>
              <v-col cols="12" sm="6">
                <s-date label="Fecha Compromiso Entrega" v-model="movedState.item.UsoCommitmentDate"></s-date>
              </v-col>
              <v-col cols="12" sm="6">
                <s-text label="Puntos de Historia (Story Points)" v-model="movedState.item.UsoStoryPoints" type="number"></s-text>
              </v-col>
            </template>

            <!-- Campos para Fase 4: En Desarrollo -->
            <template v-if="movedState.newStatus === 4">
              <v-col cols="12" sm="6">
                <s-select-user label="Desarrollador Responsable TI" v-model="movedState.item.UsoAssignedIT"></s-select-user>
              </v-col>
              <v-col cols="12" sm="6">
                <s-date label="Fecha Inicio Desarrollo" v-model="movedState.item.UsoDevStartDate"></s-date>
              </v-col>
              <v-col cols="12">
                <s-textarea label="Observaciones de Ejecución" v-model="movedState.item.UsoObservations" placeholder="Detalle técnico u observaciones..."></s-textarea>
              </v-col>
            </template>

            <!-- Campos para Fase 5: En Pruebas QA -->
            <template v-if="movedState.newStatus === 5">
              <v-col cols="12" sm="6">
                <s-select-user label="Responsable de Pruebas QA" v-model="movedState.item.UsoQAResponsible"></s-select-user>
              </v-col>
              <v-col cols="12" sm="6">
                <s-date label="Fecha Inicio Pruebas QA" v-model="movedState.item.UsoQAStartDate"></s-date>
              </v-col>
              <v-col cols="12">
                <s-text label="Enlace / Ruta de Evidencia QA" v-model="movedState.item.UsoQAEvidence" placeholder="Ruta o enlace a evidencias QA..."></s-text>
              </v-col>
            </template>

            <!-- Campos para Fase 6: Pruebas Usuario UAT -->
            <template v-if="movedState.newStatus === 6">
              <v-col cols="12" sm="6">
                <s-select-user label="Responsable UAT (Usuario / PO)" v-model="movedState.item.UsoUATResponsible"></s-select-user>
              </v-col>
              <v-col cols="12" sm="6">
                <s-date label="Fecha Inicio UAT" v-model="movedState.item.UsoUATStartDate"></s-date>
              </v-col>
              <v-col cols="12">
                <s-text label="Evidencia / Conformidad UAT" v-model="movedState.item.UsoUATEvidence" placeholder="Documento o enlace de aprobación UAT..."></s-text>
              </v-col>
            </template>

            <!-- Campos para Fase 7: Listo para Producción -->
            <template v-if="movedState.newStatus === 7">
              <v-col cols="12" sm="4">
                <s-date label="Fecha Listo para Prod" v-model="movedState.item.UsoReadyForProdDate"></s-date>
              </v-col>
              <v-col cols="12" sm="4">
                <s-text label="Versión de Despliegue" v-model="movedState.item.UsoDeployVersion" placeholder="v1.2.0"></s-text>
              </v-col>
              <v-col cols="12" sm="4">
                <s-select-user label="Desplegado Por" v-model="movedState.item.UsoDeployedBy"></s-select-user>
              </v-col>
            </template>

            <v-col cols="12" class="mt-2">
              <s-textarea label="Comentario de Transición (Opcional)" v-model="movedState.item.HtyComment" placeholder="Detalle observaciones de la transición..."></s-textarea>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pa-3 grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="cancelMove">Cancelar (Snap-back)</v-btn>
          <v-btn color="indigo" dark :loading="savingMove" @click="confirmMove">
            <v-icon left>mdi-check</v-icon> Confirmar y Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal 2: Intercepción de Retroceso (Return Dialog) -->
    <v-dialog v-model="returnDialog" max-width="600px" persistent>
      <v-card v-if="movedState" class="rounded-lg">
        <v-card-title class="headline orange lighten-5 orange--text text--darken-4 d-flex align-center">
          <v-icon color="orange darken-3" class="mr-2">mdi-arrow-left-bold-circle</v-icon>
          Devolución de Fase: {{ movedState.item.UsoCode }}
        </v-card-title>

        <v-card-text class="pa-4">
          <v-alert type="warning" text dense class="mb-4">
            Retroceso de fase detectado: <strong>{{ getStatusName(movedState.oldStatus) }}</strong> &rarr; <strong>{{ getStatusName(movedState.newStatus) }}</strong>. Debe ingresar la justificación técnica u operativa.
          </v-alert>

          <s-textarea
            label="Motivo de Devolución / Rechazo (Obligatorio)"
            v-model="movedState.returnReason"
            placeholder="Describa la causa, fallo o requerimiento pendiente por el cual se devuelve el ítem..."
          ></s-textarea>
        </v-card-text>

        <v-card-actions class="pa-3 grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="cancelMove">Cancelar (Snap-back)</v-btn>
          <v-btn color="orange darken-3" dark :loading="savingMove" @click="confirmMove">
            <v-icon left>mdi-undo</v-icon> Registrar Devolución
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogo para Gestionar Bloqueo y Próxima Acción -->
    <v-dialog v-model="blockDialog" max-width="600px" persistent>
      <v-card v-if="selectedItem" class="rounded-lg">
        <v-card-title class="headline grey lighten-3 indigo--text text--darken-3">
          <v-icon color="indigo" class="mr-2">mdi-shield-alert</v-icon>
          Gestión de Bloqueos - {{ selectedItem.UsoCode }}
        </v-card-title>

        <v-card-text class="pa-4">
          <v-switch
            v-model="selectedItem.UsoIsBlocked"
            label="¿La Historia se encuentra Bloqueada?"
            color="red"
            class="font-weight-bold"
          ></v-switch>

          <v-row v-if="selectedItem.UsoIsBlocked">
            <v-col cols="12">
              <s-textarea label="Motivo de Bloqueo" v-model="selectedItem.UsoBlockReason" placeholder="Indique la causa o impedimento..."></s-textarea>
            </v-col>
            <v-col cols="12">
              <s-textarea label="Próxima Acción / Plan de Mitigación" v-model="selectedItem.UsoNextAction" placeholder="Plan de acción para resolver el bloqueo..."></s-textarea>
            </v-col>
            <v-col cols="12" sm="6">
              <s-date label="Fecha Próxima Acción" v-model="selectedItem.UsoNextActionDate"></s-date>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pa-3 grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="blockDialog = false">Cancelar</v-btn>
          <v-btn color="indigo" dark @click="saveBlockStatus">
            <v-icon left>mdi-content-save</v-icon> Guardar Estado
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import draggable from "vuedraggable";
import _sUserStoryService from "@/services/InformationTechnology/TicUserStoryService.js";
import _sSprintService from "@/services/InformationTechnology/TicSprintService.js";
import sTextarea from "../../../components/Utils/sTextarea.vue";
import SSelectUser from "@/components/Utils/SSelectUser.vue";

export default {
  name: "KanbanBoard",
  components: {
    draggable,
    sTextarea,
    SSelectUser
  },
  data() {
    return {
      items: [],
      sprints: [],
      isKanbanAdmin: false,
      blockDialog: false,
      transitionDialog: false,
      returnDialog: false,
      savingMove: false,
      selectedItem: null,
      movedState: null,
      myTasksOnly: false,
      selectedSprintFilter: null, // { value: 'active' | 'none' | 'overdue' | 'all' | <TspID>, text }
      filter: {
        UsoTitle: "",
        ExcludeTerminalStates: true,
        UsoAssignedIT: null,
        ReqDateFrom: null,
        ReqDateUntil: null
      },
      // Columnas 1 (Solicitado/Triage) y 2 (En Refinamiento) quedan COMENTADAS por el momento.
      // Para reactivarlas, descomentar las dos primeras entradas.
      columns: [
        // { id: 1, title: "Solicitado / Triage", status: 1, color: "#2196F3", icon: "mdi-inbox-arrow-down" },
        // { id: 2, title: "En Refinamiento", status: 2, color: "#FF9800", icon: "mdi-account-edit" },
        { id: 3, title: "Listo para Desarrollo", status: 3, color: "#673AB7", icon: "mdi-playlist-check" },
        { id: 4, title: "En Desarrollo", status: 4, color: "#00BCD4", icon: "mdi-code-tags" },
        { id: 5, title: "En Pruebas (QA)", status: 5, color: "#009688", icon: "mdi-bug-check" },
        { id: 6, title: "Pruebas Usuario (UAT)", status: 6, color: "#8BC34A", icon: "mdi-account-check" },
        { id: 7, title: "Listo para Producción", status: 7, color: "#4CAF50", icon: "mdi-rocket-launch" }
      ],
      statusNames: {
        1: "Solicitado / Triage",
        2: "En Refinamiento",
        3: "Listo para Desarrollo",
        4: "En Desarrollo",
        5: "En Pruebas (QA)",
        6: "Pruebas Usuario (UAT)",
        7: "Listo para Producción",
        8: "En Producción",
        9: "Cancelado"
      }
    };
  },
  computed: {
    sprintFilterOptions() {
      const options = [];
      const activeSprint = this.sprints.find(s => s.TspStatus === 2);
      if (activeSprint) {
        options.push({ text: `Sprint Actual: ${activeSprint.TspName}`, value: "active" });
      }
      options.push({ text: "Todos los sprints (actual + pasados)", value: "all" });
      options.push({ text: "Atrasadas de sprints pasados", value: "overdue" });
      options.push({ text: "Sin sprint", value: "none" });

      const today = this.$moment().startOf('day');
      this.sprints
        .filter(s => s.TspStatus !== 2 && this.$moment(s.TspStartDate).startOf('day') <= today)
        .slice()
        .sort((a, b) => new Date(b.TspStartDate) - new Date(a.TspStartDate))
        .forEach(s => {
          options.push({ text: s.TspName, value: `sprint-${s.TspID}` });
        });
      return options;
    },
    assignableSprints() {
      // Para asignar en la transición a 'Listo para Desarrollo': sprints activos o futuros cercanos
      return this.sprints.filter(s => s.SecStatus === 1);
    },
    visibleItems() {
      let result = this.items;

      if (this.selectedSprintFilter) {
        const value = this.selectedSprintFilter;
        const today = this.$moment().startOf('day');

        if (value === "active") {
          const active = this.sprints.find(s => s.TspStatus === 2);
          if (active) {
            result = result.filter(i => i.UsoSprintID === active.TspID);
          }
        } else if (value === "none") {
          result = result.filter(i => !i.UsoSprintID);
        } else if (value === "overdue") {
          // Historias de sprints pasados (fecha fin < hoy) que aún no terminan (estado < 8)
          const finishedSprintIds = this.sprints
            .filter(s => s.TspEndDate && this.$moment(s.TspEndDate).startOf('day') < today)
            .map(s => s.TspID);
          result = result.filter(i => finishedSprintIds.includes(i.UsoSprintID) && i.UsoStatus < 8);
        } else if (typeof value === "string" && value.startsWith("sprint-")) {
          const tspID = Number(value.replace("sprint-", ""));
          result = result.filter(i => i.UsoSprintID === tspID);
        }
        // "all" -> sin filtro de sprint
      }

      // Filtro por rango de fechas del requerimiento padre
      if (this.filter.ReqDateFrom || this.filter.ReqDateUntil) {
        const from = this.filter.ReqDateFrom ? this.$moment(this.filter.ReqDateFrom).startOf('day') : null;
        const until = this.filter.ReqDateUntil ? this.$moment(this.filter.ReqDateUntil).endOf('day') : null;
        result = result.filter(i => {
          if (!i.ReqDate) return true;
          const date = this.$moment(i.ReqDate);
          if (from && date < from) return false;
          if (until && date > until) return false;
          return true;
        });
      }

      return result;
    }
  },
  created() {
    this.loadKanbanAdminFlag();
    this.loadSprints();
    this.loadKanbanData();
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      return this.$moment(date).format("DD/MM/YYYY");
    },
    getDeviationColor(days) {
      if (days == null) return "grey";
      if (days >= 0) return "green darken-1";
      if (days >= -3) return "orange darken-2";
      return "red darken-1";
    },
    getComplianceLabel(days) {
      if (days == null) return "-";
      if (days < 0) return `Vencido (${days} d)`;
      if (days === 0) return "Vence Hoy";
      return `+${days} d`;
    },
    getStatusName(status) {
      return this.statusNames[status] || `Fase ${status}`;
    },
    loadKanbanAdminFlag() {
      _sUserStoryService.isKanbanAdmin(Number(this.$fun.getUserID()))
        .then(response => {
          this.isKanbanAdmin = !!(response.data);
        })
        .catch(() => {
          this.isKanbanAdmin = false;
        });
    },
    loadSprints() {
      _sSprintService.list({}, Number(this.$fun.getUserID()))
        .then(response => {
          this.sprints = response.data || [];
          if (!this.selectedSprintFilter) {
            const active = this.sprints.find(s => s.TspStatus === 2);
            this.selectedSprintFilter = active ? "active" : "all";
          }
        })
        .catch(() => {
          this.sprints = [];
          this.selectedSprintFilter = "all";
        });
    },
    applySprintFilter() {
      // El filtro se aplica en el computed visibleItems
    },
    applyDateFilter() {
      // El filtro se aplica en el computed visibleItems
    },
    loadKanbanData() {
      this.filter.UsoAssignedIT = this.myTasksOnly ? this.$fun.getUserID() : null;

      const payload = {
        start: 0,
        length: 300,
        search: { value: "" },
        filter: {
          UsoTitle: this.filter.UsoTitle,
          UsoAssignedIT: this.filter.UsoAssignedIT,
          ExcludeTerminalStates: this.filter.ExcludeTerminalStates
        }
      };

      _sUserStoryService.pagination(payload, this.$fun.getUserID())
        .then(response => {
          if (response.status === 200 && response.data) {
            this.items = response.data.data || [];
          }
        })
        .catch(err => {
          console.error("Error loading kanban items:", err);
        });
    },
    getColumnItems(status) {
      // Orden dentro de la columna: prioridad del requerimiento desc, puntos desc, código asc
      return this.visibleItems
        .filter(item => item.UsoStatus === status)
        .slice()
        .sort((a, b) => {
          const priorityA = a.ReqInitialPriority == null ? 99 : Number(a.ReqInitialPriority);
          const priorityB = b.ReqInitialPriority == null ? 99 : Number(b.ReqInitialPriority);
          if (priorityA !== priorityB) return priorityA - priorityB;
          const pointsA = Number(a.UsoStoryPoints) || 0;
          const pointsB = Number(b.UsoStoryPoints) || 0;
          if (pointsA !== pointsB) return pointsB - pointsA;
          return String(a.UsoCode).localeCompare(String(b.UsoCode));
        });
    },
    onCardMoved(evt, targetStatus) {
      if (evt.added) {
        const item = evt.added.element;
        const oldStatus = item.UsoStatus;
        const newStatus = targetStatus;

        if (oldStatus === newStatus) return;

        const currentUserID = Number(this.$fun.getUserID());

        // Gatekeeper 1: Propiedad. Solo el asignado o un administrador puede mover la tarjeta.
        if (item.UsoAssignedIT != null && Number(item.UsoAssignedIT) !== currentUserID && !this.isKanbanAdmin) {
          this.$fun.alert("Solo el programador asignado o un administrador puede mover esta historia.", "warning");
          this.cancelMove();
          return;
        }

        // Gatekeeper 2: WIP. Máximo 1 historia en desarrollo por programador.
        if (newStatus === 4) {
          const myInDev = this.items.filter(
            i => i.UsoStatus === 4 && i.UsoAssignedIT === currentUserID && i.UsoID !== item.UsoID
          ).length;
          if (myInDev >= 1) {
            this.$fun.alert("Ya tiene una historia en desarrollo (WIP máximo: 1). Termine su tarea actual antes de tomar otra.", "warning");
            this.cancelMove();
            return;
          }
        }

        this.movedState = {
          item: Object.assign({}, item),
          originalItem: item,
          oldStatus: oldStatus,
          newStatus: newStatus,
          returnReason: ""
        };

        if (newStatus < oldStatus) {
          this.returnDialog = true;
        } else {
          this.transitionDialog = true;
        }
      }
    },
    confirmMove() {
      if (!this.movedState || !this.movedState.item) return;

      if (this.movedState.newStatus < this.movedState.oldStatus && !this.movedState.returnReason.trim()) {
        this.$fun.alert("Debe especificar el motivo de la devolución.", "warning");
        return;
      }

      const currentUserID = Number(this.$fun.getUserID());
      const item = this.movedState.item;

      const movePayload = {
        UsoID: item.UsoID,
        UsoNewStatus: this.movedState.newStatus,
        UsrUpdateID: currentUserID,
        UshComment: this.movedState.returnReason.trim()
          ? `[DEVOLUCIÓN] ${this.movedState.returnReason.trim()}`
          : (item.HtyComment || null),
        UshReturnReason: this.movedState.returnReason.trim() ? 1 : null
      };

      this.savingMove = true;

      // 1. Movimiento de estado validado por el SP (propiedad + WIP)
      _sUserStoryService.moveKanban(movePayload, currentUserID)
        .then(() => {
          // 2. Guardar los campos adicionales de la transición (fechas, responsables, etc.)
          const savePayload = Object.assign({}, item);
          savePayload.UsoStatus = this.movedState.newStatus;
          savePayload.UsrUpdateID = currentUserID;
          return _sUserStoryService.save(savePayload, currentUserID);
        })
        .then(() => {
          this.$fun.alert("Fase actualizada exitosamente", "success");
          this.transitionDialog = false;
          this.returnDialog = false;
          this.movedState = null;
          this.loadKanbanData();
        })
        .catch(error => {
          console.error(error);
          this.$fun.alert(error?.response?.data?.Message || "Error al actualizar la fase de la historia", "error");
          this.cancelMove();
        })
        .finally(() => {
          this.savingMove = false;
        });
    },
    cancelMove() {
      this.transitionDialog = false;
      this.returnDialog = false;
      this.movedState = null;
      this.loadKanbanData();
    },
    openBlockDialog(item) {
      this.selectedItem = Object.assign({}, item);
      this.blockDialog = true;
    },
    saveBlockStatus() {
      if (!this.selectedItem) return;
      const currentUserID = Number(this.$fun.getUserID());
      this.selectedItem.UsrUpdateID = currentUserID;
      if (this.selectedItem.UsoIsBlocked) {
        this.selectedItem.UsoBlockedBy = currentUserID;
        this.selectedItem.UsoBlockDate = this.$moment().format("YYYY-MM-DD HH:mm:ss");
      }

      _sUserStoryService.save(this.selectedItem, currentUserID)
        .then(response => {
          if (response.status === 200) {
            this.$fun.alert("Estado de bloqueo actualizado correctamente", "success");
            this.blockDialog = false;
            this.loadKanbanData();
          }
        })
        .catch(error => {
          console.error(error);
          this.$fun.alert(error?.response?.data?.Message || "Error al actualizar el bloqueo", "error");
        });
    }
  }
};
</script>

<style scoped>
.kanban-scroll-wrapper {
  overflow-x: auto;
  white-space: nowrap;
}
.kanban-column-item {
  min-width: 320px;
  max-width: 340px;
  flex: 0 0 auto;
}
.kanban-column-body {
  min-height: 520px;
  max-height: 75vh;
  overflow-y: auto;
}
.kanban-draggable-zone {
  min-height: 480px;
}
.hover-card:hover {
  transform: translateY(-2px);
  transition: transform 0.2s ease-in-out;
}
.cursor-move {
  cursor: move;
}
.ghost-card {
  opacity: 0.4;
  border: 2px dashed #3f51b5 !important;
  background-color: #e8eaf6 !important;
}
.border-top-accent {
  border-top-width: 4px !important;
}
.border-red-blocked {
  border-left: 4px solid #f44336 !important;
}
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
